export const getTitle = ({
  companyName,
  companyShortName,
  tickerSymbol,
  exchangeTicker,
  uniqueSymbol,
  lastPredictionYearFormatted,
  section,
  companyVariant,
}: {
  companyName: string;
  companyShortName: string;
  tickerSymbol: string;
  exchangeTicker: string;
  uniqueSymbol: string;
  lastPredictionYearFormatted: string;
  section: string;
  companyVariant: string;
}) => {
  switch (section) {
    case 'summary':
      if (['0', '1', '2', '3', '4', '5', '6', '7'].includes(companyVariant)) {
        return `${companyName} (${uniqueSymbol}) Stock Price & Quote Analysis`;
      } else {
        return `${companyShortName} (${exchangeTicker}) - Stock Price, News & Analysis`;
      }
    case 'valuation':
      return `${companyName} (${uniqueSymbol}) Stock Valuation`;
    case 'future':
      if (['0', '1', '2', '3', '4', '5'].includes(companyVariant)) {
        return `${companyName} (${uniqueSymbol}) Future Growth Forecast`;
      } else if (['6', '7', '8', '9', 'a'].includes(companyVariant)) {
        return `${companyShortName} (${exchangeTicker}) Earnings & Revenue Stock Forecast ${lastPredictionYearFormatted}`;
      } else {
        return `${companyShortName} (${tickerSymbol}) Analysts Prediction, Stock Forecast & Price Targets`;
      }
    case 'past':
      if (['0', '1', '2', '3', '4', '5'].includes(companyVariant)) {
        return `${companyName} (${uniqueSymbol}) Performance Analysis`;
      } else {
        return `${companyShortName} (${exchangeTicker}) - Earnings & Past Performance`;
      }
    case 'health':
      return `${companyName} (${uniqueSymbol}) Health & Balance Sheet`;
    case 'dividend':
      if (['0', '1', '2', '3', '4', '5'].includes(companyVariant)) {
        return `${companyName} (${uniqueSymbol}) Dividend Yield & Analysis`;
      } else {
        return `${companyShortName} (${exchangeTicker}) Dividend Yield, History and Growth`;
      }
    case 'management':
      return `${companyName} (${tickerSymbol}) Leadership & Management Team Analysis`;
    case 'ownership':
      return `${companyName} Insider Trading & Ownership Structure`;
    case 'information':
      return `${companyName} (${tickerSymbol}) Company Information`;
    default:
      return `${companyName} (${tickerSymbol})`;
  }
};

export const getDescription = ({
  companyName,
  companyShortName,
  tickerSymbol,
  exchangeTicker,
  fundamentals,
  section,
  companyVariant,
  analystCountFormatted,
  latestEarningsFormatted,
  lastEarningsDateFormatted,
  latestEPSFormatted,
  latestGrossMarginFormatted,
  latestDividendYield,
  latestDividendYieldFormatted,
  dividendGrowing,
  dividendCovered,
  currentDividendPayoutRatioFormatted,
}: {
  companyName: string;
  companyShortName: string;
  tickerSymbol: string;
  exchangeTicker: string;
  fundamentals: string;
  section: string;
  companyVariant: string;
  analystCountFormatted: string;
  latestEarningsFormatted: string;
  lastEarningsDateFormatted: string;
  latestEPSFormatted: string;
  latestGrossMarginFormatted: string;
  latestDividendYield: number | null;
  latestDividendYieldFormatted: string;
  dividendGrowing: boolean;
  dividendCovered: boolean;
  currentDividendPayoutRatioFormatted: string;
}) => {
  switch (section) {
    case 'summary':
      if (['0', '1', '2', '3', '4', '5', '6', '7'].includes(companyVariant)) {
        return `Research ${companyName} (${tickerSymbol}) stock with daily updated analysis.`;
      } else {
        return `Should you invest in ${companyShortName} (${exchangeTicker})? ${fundamentals}`;
      }
    case 'valuation':
      return `Learn to value ${companyName} (${tickerSymbol}) stock with easy-to-understand analysis. From popular relative valuation methods to future cashflows and analyst forecasts.`;
    case 'future':
      if (['0', '1', '2', '3', '4', '5'].includes(companyVariant)) {
        return `Learn how ${companyName} (${tickerSymbol}) stock is expected to grow. Compare earnings and revenue forecasts to EPS and ROE forecasts against industry and market.`;
      } else if (['6', '7', '8', '9', 'a'].includes(companyVariant)) {
        return `Discover ${companyShortName} (${exchangeTicker}) stock earnings & revenue forecast based on ${analystCountFormatted} analysts' estimates for up to 3 years.`;
      } else {
        return `${companyShortName} (${exchangeTicker}) earnings and revenue forecasts, price targets, future return on equity. Compare ${companyName}'s growth forecast against it's industry peers.`;
      }
    case 'past':
      if (['0', '1', '2', '3', '4', '5'].includes(companyVariant)) {
        return `Learn how to understand ${companyName} (${tickerSymbol}) stock's past performance and history. Compare popular metrics against industry and market.`;
      } else if (['6', '7', '8', '9', 'a'].includes(companyVariant)) {
        return `View ${companyShortName} (${exchangeTicker}) ${lastEarningsDateFormatted} released earnings and analysis on EPS, margin, growth and return on equity, assets and capital employed.`;
      } else {
        return `${companyShortName} (${exchangeTicker}) reported earnings of ${latestEarningsFormatted} on ${lastEarningsDateFormatted} with earnings per share of ${latestEPSFormatted} and a gross margin of ${latestGrossMarginFormatted}.`;
      }
    case 'health':
      return `Learn how to understand ${companyName} (${tickerSymbol}) stock's financial health, balance sheet and how this can help you make an informed decision.`;
    case 'dividend':
      if (latestDividendYield === 0 || latestDividendYield === null) {
        return 'Company does not have a record of paying a dividend.';
      } else if (['0', '1', '2', '3', '4', '5'].includes(companyVariant)) {
        return `Learn how to understand ${companyName} (${tickerSymbol}) stock's dividends. From ex-dividend dates to payout ratio and dividend yields. Compare against industry and market.`;
      } else if (['6', '7', '8', '9', 'a'].includes(companyVariant)) {
        return `View ${companyShortName} (${exchangeTicker}) stock's dividend growth, payments and safety. From ex-dividend dates, payment schedules, analyst forecasts and payout ratios.`;
      } else {
        return `${companyShortName} (${exchangeTicker}) dividend yield is ${latestDividendYieldFormatted}. Dividend payments have ${
          dividendGrowing ? 'increased' : 'decreased'
        } over the last 10 years and are ${
          dividendCovered ? 'covered' : 'not covered'
        } by earnings with a payout ratio of ${currentDividendPayoutRatioFormatted}.`;
      }
    case 'management':
      return `Learn about ${companyName} (${tickerSymbol}) stock's management team. Comprehensive performance, salary and tenure analysis for the CEO, board and leadership team.`;
    case 'ownership':
      return `Uncover the latest insider trading activity for ${companyName} (${tickerSymbol}). Know which insiders are buying and selling along with top shareholders and ownership breakdown.`;
    case 'information':
      return `Learn more about ${companyName} (${tickerSymbol}). From their employee growth over the years and their exchange listings.`;
    default:
      return `${companyName} (${tickerSymbol})`;
  }
};
